import { useEffect, useState } from "react";
import { Alert, Button } from "reactstrap";

import RichTextEditorV2 from "../../CRM/RichTextEditorV2";
import AppSpinner from "../../../elements/AppSpinner";
import useCreateComment, {
  ConvoMetadata,
  RecordComment,
} from "../../../api/api-hooks/comments/useCreateComment";
import StrUtils from "../../../utils/StrUtils";
import { ConversationUsersMap } from "../../../api/api-hooks/comments/useFetchComments";
import { useUpdateComment } from "../../../api/api-hooks/comments/useUpdateComment";

interface Props {
  context: string;
  contextID: string | undefined;
  convoMetadata: ConvoMetadata;
  commentCount: number;
  replyingTo: RecordComment | null;
  setReplyingTo: React.Dispatch<React.SetStateAction<RecordComment | null>>;
  editComment: RecordComment | null;
  setEditComment: React.Dispatch<React.SetStateAction<RecordComment | null>>;
  usersMap: ConversationUsersMap;
}

export default function CommentsEditor({
  context,
  contextID,
  convoMetadata,
  commentCount,
  replyingTo,
  setReplyingTo,
  editComment,
  setEditComment,
  usersMap,
}: Props) {
  const [richText, setRichText] = useState("");
  const [error, setError] = useState("");

  const { createComment, creatingComment } = useCreateComment();
  const { updateComment } = useUpdateComment();

  useEffect(() => {
    if (!editComment) {
      setRichText("");
    } else {
      setRichText(editComment?.Comment?.RichText || "");
    }
  }, [editComment]);

  return (
    <div className="px-3 px-md-4">
      {replyingTo && (
        <div className="text-sm bg-superlight font-weight-500 text-dark rounded-top px-3 py-2 border-top border-left border-right d-flex justify-content-between align-items-center">
          <div>
            Replying to{" "}
            <span className="font-weight-bold text-primary">
              {usersMap[replyingTo?.CreatedBy || ""]?._RecordName || "Comment"}
            </span>
          </div>
          <div>
            <i
              className="mdi mdi-close text-danger text-lg cursor-pointer"
              onClick={() => setReplyingTo(null)}
            />
          </div>
        </div>
      )}
      {editComment && (
        <div className="text-sm bg-superlight font-weight-500 text-dark rounded-top px-3 py-2 border-top border-left border-right d-flex justify-content-between align-items-center">
          <div>Editing your comment</div>
          <div>
            <i
              className="mdi mdi-close text-danger text-lg cursor-pointer"
              onClick={() => setEditComment(null)}
            />
          </div>
        </div>
      )}
      <div style={{ height: 100 }} className="mt-2">
        <RichTextEditorV2
          isEditing={true}
          value={richText}
          onChange={(val: string) => setRichText(val)}
          style={{ height: 100 }}
          useMentions={true}
          useHeader={false}
          useColor={false}
          useBlock={false}
        />
      </div>
      <div
        className="d-flex justify-content-end position-relative"
        style={{ marginTop: 45 }}
      >
        {error && (
          <Alert
            className="px-2 py-1 position-absolute"
            style={{ right: 40 }}
            color="danger"
          >
            {error}
          </Alert>
        )}
        <Button
          className="btn-icon-only"
          color="primary"
          outline
          onClick={handleCreateComment}
          size="sm"
          disabled={creatingComment}
        >
          {creatingComment ? (
            <AppSpinner size="sm" />
          ) : (
            <i className="mdi mdi-send-variant" />
          )}
        </Button>
      </div>
    </div>
  );

  function handleCreateComment() {
    setError("");

    if (!richText) {
      setError("Please type a message to continue");
      setTimeout(() => setError(""), 3000);
      return;
    }

    const mentionedUsers = StrUtils.getMentionedUserIds(richText);

    if (editComment) {
      if (!contextID) return;

      updateComment({
        context,
        contextID,
        comment: {
          RichText: richText,
          PlainText: StrUtils.convertMarkupToPlaintext(richText),
        },
        commentID: editComment._id!,
        mentionedUsers: mentionedUsers,
      });

      setRichText("");
      setEditComment(null);

      return;
    }

    createComment({
      comment: {
        comment: {
          RichText: richText,
          PlainText: StrUtils.convertMarkupToPlaintext(richText),
        },
        context: context,
        contextID: contextID!,
        parentComment: replyingTo
          ? {
              _id: replyingTo._id!,
              CreatedBy: replyingTo.CreatedBy!,
              Created: replyingTo.Created!,
              Comment: StrUtils.convertMarkupToPlaintext(
                replyingTo.Comment.RichText
              ),
            }
          : undefined,
        mentionedUsers: mentionedUsers,
        commentCount: commentCount,
      },
      convoMetadata: convoMetadata,
      onSuccess: () => {
        setRichText("");
        setReplyingTo(null);
      },
      onError: setError,
    });
  }
}
